import { getProjectsListQueryOptions } from '@/api/project';
import { Card, LoadingSpinner, Typography } from '@analytical-alley/ui';
import { IconArrowRight, IconFolder } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AiOutlineCalendar, AiOutlineReload } from 'react-icons/ai';

export const ProjectsList = () => {
    const navigate = useNavigate();
    const {
        data: projects,
        isLoading,
        isError,
        error,
    } = useQuery(getProjectsListQueryOptions());

    if (isLoading) {
        return (
            <div className="flex justify-center items-center p-8">
                <LoadingSpinner />
            </div>
        );
    }

    if (isError || !projects) {
        return (
            <Card className="p-8 mt-8">
                <Typography
                    variant="bodyM"
                    color="white"
                    className="text-center"
                >
                    Unable to load projects. Please try again later.
                    {error && (
                        <span className="block mt-2 text-sm opacity-70">
                            Error: {error.message || 'Unknown error'}
                        </span>
                    )}
                </Typography>
            </Card>
        );
    }

    if (projects.length === 0) {
        return (
            <Card className="p-8 mt-8">
                <div className="flex flex-col items-center gap-4">
                    <IconFolder className="text-gray-500" size={48} />
                    <Typography
                        variant="titleS"
                        color="white"
                        className="text-center"
                    >
                        No Projects Available
                    </Typography>
                    <Typography
                        variant="bodyM"
                        color="white"
                        className="text-center opacity-70 max-w-md"
                    >
                        You don&apos;t have access to any projects yet. Please
                        check your email for project invitations or contact your
                        administrator to get access.
                    </Typography>
                </div>
            </Card>
        );
    }

    return (
        <div className="mt-8 space-y-4">
            <Typography variant="titleL" color="white" className="mb-4">
                Your Projects
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {projects.map((project) => (
                    <div
                        key={project.id}
                        className="cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            navigate({
                                to: '/project/$projectId',
                                params: { projectId: project.id },
                            });
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate({
                                    to: '/project/$projectId',
                                    params: { projectId: project.id },
                                });
                            }
                        }}
                    >
                        <Card className="hover:bg-gray-100/15 transition-colors h-full">
                            <div className="flex flex-col gap-4 h-full">
                                <Typography
                                    variant="titleM"
                                    className="truncate"
                                >
                                    {project.name}
                                </Typography>

                                {project.interval && (
                                    <div>
                                        <span className="px-3 py-1 bg-white/10 text-white text-sm rounded-full">
                                            {project.interval}
                                        </span>
                                    </div>
                                )}

                                <div className="flex flex-col gap-3">
                                    {project.startDate && project.endDate && (
                                        <div className="flex items-start gap-2">
                                            <AiOutlineCalendar
                                                className="shrink-0 mt-0.5 text-emerald-400"
                                                size={20}
                                            />
                                            <div>
                                                <Typography
                                                    variant="bodyS"
                                                    className="font-medium"
                                                >
                                                    Model period:
                                                </Typography>
                                                <Typography
                                                    variant="bodyS"
                                                    className="opacity-90"
                                                >
                                                    {new Date(
                                                        project.startDate,
                                                    ).toLocaleDateString(
                                                        'en-GB',
                                                        {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                        },
                                                    )}{' '}
                                                    -{' '}
                                                    {new Date(
                                                        project.endDate,
                                                    ).toLocaleDateString(
                                                        'en-GB',
                                                        {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                        },
                                                    )}
                                                </Typography>
                                            </div>
                                        </div>
                                    )}

                                    {project.lastUpdatedAt && (
                                        <div className="flex items-start gap-2">
                                            <AiOutlineReload
                                                className="shrink-0 mt-0.5 text-emerald-400"
                                                size={20}
                                            />
                                            <div>
                                                <Typography
                                                    variant="bodyS"
                                                    className="font-medium"
                                                >
                                                    Last update:
                                                </Typography>
                                                <Typography
                                                    variant="bodyS"
                                                    className="opacity-90"
                                                >
                                                    {new Date(
                                                        project.lastUpdatedAt,
                                                    ).toLocaleDateString(
                                                        'en-GB',
                                                        {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                        },
                                                    )}
                                                </Typography>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <button className="mt-auto w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2">
                                    View Dashboard
                                    <IconArrowRight size={20} />
                                </button>
                            </div>
                        </Card>
                    </div>
                ))}
            </div>
        </div>
    );
};
